import { Component } from '@theme/component';

/**
 * Scroll-snap slideshow that loops without rewinding.
 *
 * The viewport is a real horizontally-scrolling element, so touch swipe, keyboard scrolling and
 * the arrow buttons all drive the same mechanism — the JS only nudges `scrollLeft` and mirrors
 * the resulting position onto the dots.
 *
 * Looping is done with cloned edge slides rather than by wrapping the index. Wrapping the index
 * meant "next" on the last slide scrolled all the way back to the left, travelling through every
 * slide in between; a clone of the first slide sits after the last one, so the movement continues
 * in the same direction and the jump back to the real slide happens once the scroll has settled,
 * with no animation to see.
 *
 * @extends {Component<{
 *   viewport: HTMLElement,
 *   slides: HTMLElement[],
 *   dots: HTMLButtonElement[],
 * }>}
 */
class OudhSlideshow extends Component {
  /** Real slides, captured before any cloning. */
  #count = 0;
  /** Position in the DOM, including the clones: 0 is the trailing slide's clone. */
  #dom = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;
  #scrollRaf = 0;
  /** Last viewport width seen, so a height-only change is ignored. */
  #lastWidth = 0;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#count = this.refs.slides?.length ?? 0;
    this.#clone();

    this.refs.viewport?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#play);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#play);

    // The viewport's width is the slide width, so a position has to be recomputed when it changes
    // — a phone rotating would otherwise leave the rail halfway between two slides.
    //
    // Width only. A ResizeObserver reports every size change, and the viewport grows in height as
    // the hero images arrive; realigning on those cancelled the in-flight smooth scroll, which
    // pinned the slideshow on its first slide however often the timer fired.
    if (this.refs.viewport) {
      this.#resizeObserver = new ResizeObserver(() => {
        const width = this.#width;
        if (width === 0 || width === this.#lastWidth) return;

        const first = this.#lastWidth === 0;
        this.#lastWidth = width;
        if (!first) this.#jumpTo(this.#dom);
      });
      this.#resizeObserver.observe(this.refs.viewport);
    }

    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.viewport?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#settleTimer);
    this.#pause();
  }

  get #looping() {
    return this.#count > 1;
  }

  get #width() {
    return this.refs.viewport?.clientWidth ?? 0;
  }

  /** Which real slide is on screen, whichever DOM position — including a clone — is showing. */
  get #index() {
    if (!this.#looping) return this.#dom;
    if (this.#dom === 0) return this.#count - 1;
    if (this.#dom === this.#count + 1) return 0;
    return this.#dom - 1;
  }

  /**
   * Puts a copy of the last slide before the first and a copy of the first after the last, then
   * parks the viewport on the real first slide.
   *
   * The copies carry no `ref`: Horizon keeps `refs` in sync through a MutationObserver, so a
   * cloned `ref="slides[]"` would join `refs.slides` and inflate the count. They are also `inert`,
   * which keeps their buttons out of the tab order and their text out of the accessibility tree.
   */
  #clone() {
    const { viewport, slides } = this.refs;
    if (!viewport || !this.#looping || !slides) return;

    const first = slides[0];
    const last = slides[slides.length - 1];
    if (!first || !last) return;

    viewport.append(this.#copy(first));
    viewport.prepend(this.#copy(last));

    // Layout has to exist before a scroll position means anything.
    requestAnimationFrame(() => this.#jumpTo(1));
  }

  /** @param {HTMLElement} slide */
  #copy(slide) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
    clone.removeAttribute('ref');
    for (const node of clone.querySelectorAll('[ref]')) node.removeAttribute('ref');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-clone', '');
    clone.inert = true;
    return clone;
  }

  #play = () => {
    if (this.dataset.autoplay !== 'true' || this.#count < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#pause();
    this.#timer = setInterval(() => this.next(), Number(this.dataset.interval) || 6000);
  };

  #pause = () => {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;
  };

  /** Derives the position from scroll rather than tracking it independently. */
  #onScroll = () => {
    if (this.#scrollRaf) return;

    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = 0;

      const width = this.#width;
      if (width === 0) return;

      const dom = Math.round((this.refs.viewport?.scrollLeft ?? 0) / width);
      if (dom !== this.#dom) {
        this.#dom = dom;
        this.#syncDots();
      }

      this.#scheduleSettle();
    });
  };

  /**
   * Landing on a clone is only ever a waypoint. Once the scroll has stopped — whether it was a
   * button, a swipe or momentum that got here — the viewport is moved to the real slide showing
   * the same thing, with no animation, so nothing is visible but the continued travel.
   */
  #scheduleSettle() {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#normalise(), 140);
  }

  /**
   * Moves off a clone and onto the real slide it copies.
   *
   * Also called before every step, not only on the timer: a swipe onto a clone followed by a press
   * inside the settle window would otherwise step to a position past the end of the rail, which
   * nothing afterwards recognises as a clone — the slideshow would stop there for good.
   */
  #normalise() {
    if (!this.#looping) return;
    if (this.#dom === 0) this.#jumpTo(this.#count);
    else if (this.#dom === this.#count + 1) this.#jumpTo(1);
  }

  /**
   * Moves without animation. `behavior: 'instant'` is explicit because the viewport sets
   * `scroll-behavior: smooth` in CSS, which `scrollTo` would otherwise honour.
   *
   * @param {number} dom
   */
  #jumpTo(dom) {
    const { viewport } = this.refs;
    const width = this.#width;
    if (!viewport || width === 0) return;

    this.#dom = dom;
    viewport.scrollTo({ left: width * dom, behavior: 'instant' });
    this.#syncDots();
  }

  /** @param {number} dom */
  #slideTo(dom) {
    const { viewport } = this.refs;
    const width = this.#width;
    if (!viewport || width === 0) return;

    viewport.scrollTo({ left: width * dom, behavior: 'smooth' });
  }

  #syncDots() {
    const index = this.#index;
    this.refs.dots?.forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === index));
    });
  }

  /**
   * @param {number} index - A real slide, 0-based.
   *
   * Jumping straight to a distant slide is what a dot means, so this does not go via the clones.
   */
  goTo(index) {
    if (this.#count === 0) return;

    const target = ((index % this.#count) + this.#count) % this.#count;
    this.#slideTo(this.#looping ? target + 1 : target);
  }

  next() {
    if (!this.#looping) return;
    this.#normalise();
    this.#slideTo(this.#dom + 1);
  }

  previous() {
    if (!this.#looping) return;
    this.#normalise();
    this.#slideTo(this.#dom - 1);
  }

  /**
   * @param {Event} event
   *
   * `event.target`, not `currentTarget`: the listener lives on `document`, so `currentTarget` is
   * the document during dispatch and null afterwards. Horizon rewrites `target` to the element
   * carrying the `on:click`.
   */
  goToDot(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const button = event.target.closest('[data-index]');
    if (!(button instanceof HTMLElement)) return;

    this.goTo(Number(button.dataset.index));
    this.#pause();
  }
}

if (!customElements.get('oudh-slideshow')) {
  customElements.define('oudh-slideshow', OudhSlideshow);
}
